import { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { money, fmtDDMMYYYY } from '../../lib/format';
import ServiceOrderModal from './ServiceOrderModal';
import {
  IconServices, IconPlus, IconEdit, IconTrash, IconCalendar
} from '../components/SvgIcons';



const WORK_STATUS_COLORS = {
  'In Progress': { bg: 'var(--crm-blue-bg)', color: 'var(--crm-blue-text)', border: 'var(--crm-blue-border)' },
  'Completed': { bg: 'var(--crm-green-bg)', color: 'var(--crm-green-text)', border: 'var(--crm-green-border)' },
  'Waiting to Start': { bg: 'var(--crm-amber-bg)', color: 'var(--crm-amber-text)', border: 'var(--crm-amber-border)' },
  'Waiting for Client': { bg: 'var(--crm-purple-bg)', color: 'var(--crm-purple-text)', border: 'var(--crm-purple-border)' },
  'On Hold': { bg: 'var(--crm-red-bg)', color: 'var(--crm-red-text)', border: 'var(--crm-red-border)' },
  'Cancelled': { bg: 'var(--crm-red-bg)', color: 'var(--crm-red-text)', border: 'var(--crm-red-border)' },
};

const PAYMENT_COLORS = {
  Paid: { bg: 'var(--crm-green-bg)', color: 'var(--crm-green-text)', border: 'var(--crm-green-border)' },
  Partial: { bg: 'var(--crm-amber-bg)', color: 'var(--crm-amber-text)', border: 'var(--crm-amber-border)' },
  Pending: { bg: 'var(--crm-red-bg)', color: 'var(--crm-red-text)', border: 'var(--crm-red-border)' },
};

function StatusBadge({ label, colors }) {
  const c = colors?.[label] || { bg: 'var(--crm-bg)', color: 'var(--crm-text-muted)', border: 'var(--crm-border)' };
  return (
    <span style={{ background: c.bg, color: c.color, border: c.border ? `1px solid ${c.border}` : 'none', padding: '2px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

export default function ClientProfile({ clientId, currency, availableServices, canViewFinance = true, onBack, user }) {
  const TABS = canViewFinance
    ? ['overview', 'orders', 'calendar', 'payments']
    : ['overview', 'orders', 'calendar'];

  const TAB_LABELS = {
    overview: 'Overview',
    orders: 'Service Orders',
    calendar: 'Social Calendar',
    payments: 'Payments',
  };
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [toast, setToast] = useState('');
  const [addingOrder, setAddingOrder] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [confirmDeleteOrder, setConfirmDeleteOrder] = useState(null);
  const [addingCalendarItem, setAddingCalendarItem] = useState(false);
  const [addingPackageBusy, setAddingPackageBusy] = useState(false);

  const handleAddPremiumPackage = async () => {
    if (!window.confirm('Are you sure you want to add the Premium 3-Month Package (₹75k) to this client? This will automatically create 5 service orders (Reels, Posts, Barter, SMM, Ads).')) {
      return;
    }
    setAddingPackageBusy(true);
    const todayYMD = new Date().toISOString().split('T')[0];
    
    // Calculate end date (3 months from now)
    const endD = new Date();
    endD.setMonth(endD.getMonth() + 3);
    const pad = (n) => String(n).padStart(2, '0');
    const endDateStr = `${endD.getFullYear()}-${pad(endD.getMonth() + 1)}-${pad(endD.getDate())}`;

    const packageOrders = [
      { serviceName: 'Reels Creation', serviceId: 'srv-3', quantity: 15, unit: 'Reels', price: 15000, startDate: todayYMD, endDate: endDateStr, renewalDate: endDateStr, workStatus: 'In Progress', notes: 'Premium Package - 5 Reels per month' },
      { serviceName: 'Graphic Design', serviceId: 'srv-4', quantity: 60, unit: 'Graphics', price: 15000, startDate: todayYMD, endDate: endDateStr, renewalDate: endDateStr, workStatus: 'In Progress', notes: 'Premium Package - 20 Posts per month' },
      { serviceName: 'Barter Collaboration Reels', serviceId: '', quantity: 60, unit: 'Reels', price: 15000, startDate: todayYMD, endDate: endDateStr, renewalDate: endDateStr, workStatus: 'In Progress', notes: 'Premium Package - 20 Barter Reels per month' },
      { serviceName: 'Social Media Management', serviceId: '', quantity: 3, unit: 'Months', price: 15000, startDate: todayYMD, endDate: endDateStr, renewalDate: endDateStr, workStatus: 'In Progress', notes: 'Premium Package' },
      { serviceName: 'Meta Ads', serviceId: 'srv-1', quantity: 3, unit: 'Months', price: 15000, startDate: todayYMD, endDate: endDateStr, renewalDate: endDateStr, workStatus: 'In Progress', notes: 'Premium Package' }
    ];

    try {
      for (const order of packageOrders) {
        await api.post(`/api/client-module/clients/${clientId}/orders`, order);
      }
      showToast('Premium 3-Month Package added successfully!');
      loadClient();
    } catch (err) {
      alert('Failed to add package: ' + err.message);
    } finally {
      setAddingPackageBusy(false);
    }
  };
  const [editingCalendarItem, setEditingCalendarItem] = useState(null);
  const [staffList, setStaffList] = useState([]);

  useEffect(() => {
    api.get('/api/staff-module/staff')
      .then((data) => setStaffList(data || []))
      .catch(() => {});
  }, []);

  async function saveCalendar(newCalendar) {
    const updatedClient = { ...client, socialMediaCalendar: newCalendar };
    await api.put(`/api/client-module/clients/${clientId}`, updatedClient);
    setClient(updatedClient);
    showToast('Calendar updated successfully!');
  }

  async function handleAddCalendarItem(itemData) {
    const currentCal = client.socialMediaCalendar || [];
    const newItem = {
      id: `cal-${Date.now()}`,
      ...itemData
    };
    const updated = [...currentCal, newItem];
    await saveCalendar(updated);
    setAddingCalendarItem(false);
  }

  async function handleEditCalendarItem(itemData) {
    const currentCal = client.socialMediaCalendar || [];
    const updated = currentCal.map((item) => item.id === editingCalendarItem.id ? { ...item, ...itemData } : item);
    await saveCalendar(updated);
    setEditingCalendarItem(null);
  }

  async function handleDeleteCalendarItem(itemId) {
    if (!window.confirm('Are you sure you want to delete this calendar item?')) return;
    const currentCal = client.socialMediaCalendar || [];
    const updated = currentCal.filter((item) => item.id !== itemId);
    await saveCalendar(updated);
  }

  async function handleStatusChange(itemId, newStatus) {
    const currentCal = client.socialMediaCalendar || [];
    const updated = currentCal.map((item) => item.id === itemId ? { ...item, status: newStatus } : item);
    await saveCalendar(updated);
  }

  const getCalendarReminder = (dateStr, status) => {
    if (status === 'Uploaded') {
      return { text: 'Uploaded', color: '#16a34a', bg: '#dcfce7', icon: '✅' };
    }
    if (!dateStr) return { text: 'No date set', color: '#64748b', bg: '#f1f5f9', icon: '📅' };
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(dateStr);
    targetDate.setHours(0, 0, 0, 0);
    
    const diffTime = targetDate.getTime() - today.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { text: `Overdue by ${Math.abs(diffDays)} day(s)!`, color: '#dc2626', bg: '#fee2e2', icon: '🚨' };
    }
    if (diffDays === 0) {
      return { text: 'Uploading TODAY!', color: '#dc2626', bg: '#fee2e2', icon: '🚨' };
    }
    if (diffDays === 1) {
      return { text: 'Uploading tomorrow (1 day left)', color: '#d97706', bg: '#fef3c7', icon: '⚠️' };
    }
    if (diffDays <= 3) {
      return { text: `Uploading in ${diffDays} days left`, color: '#d97706', bg: '#fef3c7', icon: '⚠️' };
    }
    return { text: `Uploading in ${diffDays} days`, color: '#2563eb', bg: '#dbeafe', icon: '📅' };
  };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const loadClient = useCallback(() => {
    setLoading(true);
    api.get(`/api/client-module/clients/${clientId}`)
      .then((data) => { setClient(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [clientId]);

  useEffect(() => { loadClient(); }, [loadClient]);

  // ── Computed aggregates across all orders ──
  const orders = client?.serviceOrders || [];
  const totalRevenue = orders.reduce((s, o) => s + (o.finalAmount || 0), 0);
  const totalReceived = orders.reduce((s, o) => s + (o.amountReceived || 0), 0);
  const totalPending = orders.reduce((s, o) => s + (o.pendingAmount || 0), 0);
  const activeOrders = orders.filter((o) => o.workStatus === 'In Progress').length;

  // ── Service Order handlers ──
  async function handleAddOrder(formData) {
    await api.post(`/api/client-module/clients/${clientId}/orders`, formData);
    showToast('Service Order added successfully!');
    setAddingOrder(false);
    loadClient();
  }

  async function handleEditOrder(formData) {
    await api.put(`/api/client-module/clients/${clientId}/orders/${editingOrder.id}`, formData);
    showToast('Service Order updated!');
    setEditingOrder(null);
    loadClient();
  }

  async function handleDeleteOrder(orderId) {
    await api.del(`/api/client-module/clients/${clientId}/orders/${orderId}`);
    showToast('Service Order deleted');
    setConfirmDeleteOrder(null);
    loadClient();
  }


  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: 'var(--crm-text-muted)', fontWeight: 600 }}>
        Loading client profile…
      </div>
    );
  }
  if (!client) {
    return <div style={{ padding: 40 }}>Client not found. <button onClick={onBack} className="cm-btn-outline">Go Back</button></div>;
  }

  return (
    <div>
      {toast && (
        <div style={{ background: 'var(--crm-emerald-light)', color: 'var(--crm-emerald)', padding: '10px 18px', borderRadius: 10, marginBottom: 16, fontWeight: 700, border: '1px solid var(--crm-border)' }}>
          ✓ {toast}
        </div>
      )}

      {/* ── Profile Header ── */}
      <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #0f172a 100%)', borderRadius: 16, padding: '28px 32px', marginBottom: 24, color: '#fff', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', right: -40, top: -40, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>
                ← Back
              </button>
              <span style={{ background: client.status === 'Active' ? '#224eae' : '#64748b', color: '#fff', padding: '3px 12px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 700 }}>
                {client.status}
              </span>
            </div>
            <h2 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 900, letterSpacing: '-0.02em' }}>{client.clientName}</h2>
            <p style={{ margin: '4px 0 0', color: '#94a3b8', fontSize: '0.9rem' }}>
              {client.company && client.company !== client.clientName ? `${client.company} · ` : ''}
              {client.mobile || 'No phone'} · {client.email || 'No email'}
            </p>
            <p style={{ margin: '4px 0 0', color: 'var(--crm-text-muted)', fontSize: '0.8rem' }}>
              Joined: {fmtDDMMYYYY(client.joinDate)} · Ref: {client.referenceSource || 'Direct'} · ID: {client.id?.slice(-6)}
            </p>
          </div>

          {/* Aggregate Financial / Work Chips */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {canViewFinance && (
              <>
                <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: '12px 20px', textAlign: 'center', minWidth: 110 }}>
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Revenue</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 900, color: '#ffffff', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {money(totalRevenue, currency)}
                  </div>
                </div>
                <div style={{ background: 'rgba(34,197,94,0.15)', borderRadius: 12, padding: '12px 20px', textAlign: 'center', minWidth: 110 }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--crm-emerald)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Received</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 900, color: 'var(--crm-emerald)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {money(totalReceived, currency)}
                  </div>
                </div>
                <div style={{ background: 'rgba(239,68,68,0.15)', borderRadius: 12, padding: '12px 20px', textAlign: 'center', minWidth: 110 }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--crm-text-muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Pending</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 900, color: 'var(--crm-text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {money(totalPending, currency)}
                  </div>
                </div>
              </>
            )}
            <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: '12px 20px', textAlign: 'center', minWidth: 80 }}>
              <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Orders</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 900 }}>{orders.length}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tab Bar ── */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid var(--crm-border)', marginBottom: 24, overflowX: 'auto' }}>
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            style={{
              border: 'none',
              background: 'none',
              padding: '10px 18px',
              fontSize: '0.88rem',
              fontWeight: 700,
              cursor: 'pointer',
              borderBottom: activeTab === t ? '3px solid #224eae' : '3px solid transparent',
              color: activeTab === t ? '#224eae' : '#64748b',
              whiteSpace: 'nowrap',
              transition: 'all .15s',
            }}
          >
            {TAB_LABELS[t]}
            {t === 'orders' && orders.length > 0 && (
              <span style={{ background: '#224eae', color: '#fff', borderRadius: 20, fontSize: '0.65rem', padding: '1px 7px', marginLeft: 6, fontWeight: 800 }}>
                {orders.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab Content ── */}

      {/* OVERVIEW */}
      {activeTab === 'overview' && (
        <div>
          <div className="cm-grid-2" style={{ marginBottom: 20 }}>
            {/* Client Info Card */}
            <div className="cm-form-section-card">
              <div className="cm-form-section-title">Client Information</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: '0.88rem', color: 'var(--crm-text-main)' }}>
                <div><span style={{ color: 'var(--crm-text-muted)', fontWeight: 600 }}>Company:</span> {client.company || '—'}</div>
                <div><span style={{ color: 'var(--crm-text-muted)', fontWeight: 600 }}>Mobile:</span> {client.mobile || '—'}</div>
                <div><span style={{ color: 'var(--crm-text-muted)', fontWeight: 600 }}>Email:</span> {client.email || '—'}</div>
                <div><span style={{ color: 'var(--crm-text-muted)', fontWeight: 600 }}>Address:</span> {client.address || '—'}</div>
                <div><span style={{ color: 'var(--crm-text-muted)', fontWeight: 600 }}>Reference:</span> {client.referenceSource || 'Direct Inquiry'}</div>
                <div><span style={{ color: 'var(--crm-text-muted)', fontWeight: 600 }}>Join Date:</span> {fmtDDMMYYYY(client.joinDate)}</div>
                <div><span style={{ color: 'var(--crm-text-muted)', fontWeight: 600 }}>Client Since:</span> {client.createdAt ? fmtDDMMYYYY(client.createdAt) : '—'}</div>
              </div>
            </div>

            {/* Financial Summary Card */}
            {canViewFinance ? (
              <div className="cm-form-section-card">
                <div className="cm-form-section-title">Financial Summary (All Orders)</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: '0.88rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--crm-text-muted)', fontWeight: 600 }}>Total Orders:</span>
                    <strong>{orders.length}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--crm-text-muted)', fontWeight: 600 }}>Active Orders:</span>
                    <strong style={{ color: 'var(--crm-emerald)' }}>{activeOrders}</strong>
                  </div>
                  <div style={{ height: 1, background: '#e2e8f0', margin: '4px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--crm-text-muted)', fontWeight: 600 }}>Total Revenue:</span>
                    <strong>{money(totalRevenue, currency)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--crm-emerald)', fontWeight: 700 }}>Total Received:</span>
                    <strong style={{ color: 'var(--crm-emerald)' }}>{money(totalReceived, currency)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--crm-text-main)', fontWeight: 700 }}>Total Pending:</span>
                    <strong style={{ color: 'var(--crm-text-main)' }}>{money(totalPending, currency)}</strong>
                  </div>
                </div>
              </div>
            ) : (
              <div className="cm-form-section-card">
                <div className="cm-form-section-title">Work Execution Summary</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: '0.88rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--crm-text-muted)', fontWeight: 600 }}>Total Service Orders:</span>
                    <strong>{orders.length}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--crm-text-muted)', fontWeight: 600 }}>Active In-Progress Orders:</span>
                    <strong style={{ color: 'var(--crm-emerald)' }}>{activeOrders}</strong>
                  </div>
                  <div style={{ height: 1, background: '#e2e8f0', margin: '4px 0' }} />
                  <div style={{ color: 'var(--crm-emerald)', fontWeight: 700, fontSize: '0.82rem' }}>
                    🔒 Financial prices & payments are restricted to management.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Recent Orders Preview */}
          <div className="cm-form-section-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
              <div className="cm-form-section-title" style={{ marginBottom: 0 }}>Recent Service Orders</div>
              {(canViewFinance || user?.role === 'partner') && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="cm-btn-emerald" style={{ fontSize: '0.8rem', padding: '6px 14px', background: '#224eae' }} onClick={handleAddPremiumPackage} disabled={addingPackageBusy}>
                    {addingPackageBusy ? 'Adding Package…' : '🎁 + Add 3-Month Package (₹75k)'}
                  </button>
                  <button className="cm-btn-emerald" style={{ fontSize: '0.8rem', padding: '6px 14px' }} onClick={() => { setAddingOrder(true); }}>
                    + Add New Service Order
                  </button>
                </div>
              )}
            </div>
            {orders.length === 0 ? (
              <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px 0', fontSize: '0.9rem' }}>
                No service orders yet. Click "+ Add New Service Order" to start.
              </div>
            ) : (
              orders.slice().reverse().slice(0, 3).map((o) => (
                <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--crm-bg)', borderRadius: 10, marginBottom: 8, border: '1px solid var(--crm-border)' }}>
                  <div>
                    <span style={{ fontWeight: 800, color: 'var(--crm-text-main)', fontSize: '0.9rem' }}>Order #{o.orderNumber} — {o.serviceName}</span>
                    <div style={{ fontSize: '0.78rem', color: 'var(--crm-text-muted)', marginTop: 2 }}>
                      {fmtDDMMYYYY(o.startDate)} → {fmtDDMMYYYY(o.endDate)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', display: 'flex', gap: 8, alignItems: 'center' }}>
                    <StatusBadge label={o.workStatus} colors={WORK_STATUS_COLORS} />
                    {canViewFinance && <StatusBadge label={o.paymentStatus} colors={PAYMENT_COLORS} />}
                    {canViewFinance && <strong style={{ fontSize: '0.9rem' }}>{money(o.finalAmount, currency)}</strong>}
                  </div>
                </div>
              ))
            )}
            {orders.length > 3 && (
              <button className="cm-btn-outline" style={{ fontSize: '0.82rem', width: '100%', marginTop: 4 }} onClick={() => setActiveTab('orders')}>
                View all {orders.length} orders →
              </button>
            )}
          </div>
        </div>
      )}

      {activeTab === 'orders' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h3 style={{ margin: 0, fontWeight: 900, fontSize: '1.05rem' }}>Service Order History</h3>
              <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--crm-text-muted)' }}>Complete history — orders are never deleted or overwritten</p>
            </div>
            {(canViewFinance || user?.role === 'partner') && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="cm-btn-emerald" style={{ background: '#224eae' }} onClick={handleAddPremiumPackage} disabled={addingPackageBusy}>
                  {addingPackageBusy ? 'Adding Package…' : '🎁 + Add 3-Month Package (₹75k)'}
                </button>
                <button className="cm-btn-emerald" onClick={() => setAddingOrder(true)}>
                  <IconPlus size={16} /> Add New Service Order
                </button>
              </div>
            )}
          </div>

          {orders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8', background: 'var(--crm-bg)', borderRadius: 14, border: '2px dashed #e2e8f0' }}>
              <IconServices size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
              <div style={{ fontWeight: 700, fontSize: '1rem' }}>No Service Orders Yet</div>
              <div style={{ fontSize: '0.85rem', marginTop: 6 }}>Click "+ Add New Service Order" to add the first order for this client</div>
            </div>
          ) : (
            [...orders].reverse().map((o) => (
              <div key={o.id} style={{ background: 'var(--crm-card-bg)', border: '1px solid var(--crm-border)', borderRadius: 14, marginBottom: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                {/* Order Header */}
                <div style={{ padding: '14px 20px', background: 'linear-gradient(to right, #f8fafc, #f1f5f9)', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ background: '#0f172a', color: '#fff', borderRadius: 8, padding: '4px 10px', fontWeight: 900, fontSize: '0.8rem' }}>
                      #{o.orderNumber}
                    </span>
                    <div>
                      <div style={{ fontWeight: 800, color: 'var(--crm-text-main)', fontSize: '0.95rem' }}>{o.serviceName}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--crm-text-muted)' }}>
                        {o.quantity} {o.unit} · Created: {fmtDDMMYYYY(o.createdAt)}
                        {o.assignedStaff && ` · Staff: ${o.assignedStaff}`}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <StatusBadge label={o.workStatus} colors={WORK_STATUS_COLORS} />
                    {canViewFinance && <StatusBadge label={o.paymentStatus} colors={PAYMENT_COLORS} />}
                    {(canViewFinance || user?.role === 'partner') && <button className="cm-action-icon-btn" title="Edit Order" onClick={() => setEditingOrder(o)}><IconEdit size={15} /></button>}
                    {canViewFinance && <button className="cm-action-icon-btn" title="Delete Order" style={{ color: 'var(--crm-text-main)' }} onClick={() => setConfirmDeleteOrder(o)}><IconTrash size={15} /></button>}
                  </div>
                </div>

                {/* Order Body */}
                <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Start → End</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>{fmtDDMMYYYY(o.startDate)} → {fmtDDMMYYYY(o.endDate)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Renewal</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--crm-text-main)' }}>{fmtDDMMYYYY(o.renewalDate) || '—'}</div>
                  </div>

                  {canViewFinance && (
                    <>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Price</div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>{money(o.price, currency)}</div>
                      </div>
                      {o.discountAmount > 0 && (
                        <div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Discount</div>
                          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--crm-text-muted)' }}>{`- ${money(o.discountAmount, currency)}`}</div>
                        </div>
                      )}
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Final Amount</div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 900, color: 'var(--crm-emerald)' }}>{money(o.finalAmount, currency)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Received</div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--crm-emerald)' }}>{money(o.amountReceived, currency)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Pending</div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--crm-text-main)' }}>{money(o.pendingAmount, currency)}</div>
                      </div>
                    </>
                  )}
                </div>

                {o.notes && (
                  <div style={{ padding: '0 20px 14px', fontSize: '0.82rem', color: 'var(--crm-text-muted)', fontStyle: 'italic' }}>
                    📝 {o.notes}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* SOCIAL MEDIA CALENDAR */}
      {activeTab === 'calendar' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h3 style={{ margin: 0, fontWeight: 900, fontSize: '1.05rem' }}>Social Media Upload Calendar</h3>
              <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--crm-text-muted)' }}>Track reels scripting, shooting, editing, and scheduled upload dates</p>
            </div>
            <button className="cm-btn-emerald" onClick={() => setAddingCalendarItem(true)}>
              + Add Calendar Entry
            </button>
          </div>

          <div className="cm-form-section-card">
            {!(client.socialMediaCalendar && client.socialMediaCalendar.length > 0) ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8', background: 'var(--crm-bg)', borderRadius: 14, border: '2px dashed #e2e8f0' }}>
                <IconCalendar size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                <div style={{ fontWeight: 700, fontSize: '1rem' }}>No Calendar Entries Yet</div>
                <div style={{ fontSize: '0.85rem', marginTop: 6 }}>Click "+ Add Calendar Entry" to schedule the first upload</div>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--crm-border)', textAlign: 'left' }}>
                      <th style={{ padding: '12px 16px', color: 'var(--crm-text-muted)', fontWeight: 700 }}>Title / Description</th>
                      <th style={{ padding: '12px 16px', color: 'var(--crm-text-muted)', fontWeight: 700 }}>Scheduled Date</th>
                      <th style={{ padding: '12px 16px', color: 'var(--crm-text-muted)', fontWeight: 700 }}>Assigned To</th>
                      <th style={{ padding: '12px 16px', color: 'var(--crm-text-muted)', fontWeight: 700 }}>Status</th>
                      <th style={{ padding: '12px 16px', color: 'var(--crm-text-muted)', fontWeight: 700 }}>Countdown Reminder</th>
                      <th style={{ padding: '12px 16px', color: 'var(--crm-text-muted)', fontWeight: 700 }}>Notes</th>
                      <th style={{ padding: '12px 16px', color: 'var(--crm-text-muted)', fontWeight: 700, textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {client.socialMediaCalendar.map((item) => {
                      const reminder = getCalendarReminder(item.scheduledDate, item.status);
                      return (
                        <tr key={item.id} style={{ borderBottom: '1px solid var(--crm-border)' }}>
                          <td style={{ padding: '14px 16px', fontWeight: 800, color: 'var(--crm-text-main)' }}>{item.title}</td>
                          <td style={{ padding: '14px 16px', fontWeight: 700 }}>{fmtDDMMYYYY(item.scheduledDate) || '—'}</td>
                          <td style={{ padding: '14px 16px', color: 'var(--crm-text-main)' }}>{item.assignedStaff || 'Unassigned'}</td>
                          <td style={{ padding: '14px 16px' }}>
                            <select
                              value={item.status || 'Draft'}
                              onChange={(e) => handleStatusChange(item.id, e.target.value)}
                              style={{ padding: '4px 8px', borderRadius: 8, border: '1px solid var(--crm-border)', fontSize: '0.8rem', fontWeight: 700 }}
                            >
                              <option value="Draft">Draft</option>
                              <option value="Shooting">Shooting</option>
                              <option value="Editing">Editing</option>
                              <option value="Scheduled">Scheduled</option>
                              <option value="Uploaded">Uploaded</option>
                            </select>
                          </td>
                          <td style={{ padding: '14px 16px' }}>
                            <span style={{
                              background: reminder.bg,
                              color: reminder.color,
                              padding: '4px 12px',
                              borderRadius: 20,
                              fontWeight: 800,
                              fontSize: '0.78rem',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6
                            }}>
                              {reminder.icon} {reminder.text}
                            </span>
                          </td>
                          <td style={{ padding: '14px 16px', color: 'var(--crm-text-muted)', fontStyle: 'italic', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.notes}>
                            {item.notes || '—'}
                          </td>
                          <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                              <button
                                type="button"
                                className="cm-action-icon-btn"
                                title="Edit"
                                onClick={() => setEditingCalendarItem(item)}
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                              >
                                ✏️
                              </button>
                              <button
                                type="button"
                                className="cm-action-icon-btn"
                                title="Delete"
                                onClick={() => handleDeleteCalendarItem(item.id)}
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#dc2626' }}
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* PAYMENTS */}
      {activeTab === 'payments' && (
        <div>
          <div className="cm-form-section-card" style={{ marginBottom: 20 }}>
            <div className="cm-form-section-title">Payment Summary</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 14 }}>
              <div style={{ background: 'var(--crm-bg)', borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Total Revenue</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 900, marginTop: 4 }}>{money(totalRevenue, currency)}</div>
              </div>
              <div style={{ background: 'var(--crm-emerald-light)', borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--crm-emerald)', fontWeight: 700, textTransform: 'uppercase' }}>Total Received</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 900, color: 'var(--crm-emerald)', marginTop: 4 }}>{money(totalReceived, currency)}</div>
              </div>
              <div style={{ background: 'var(--crm-bg)', borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--crm-text-main)', fontWeight: 700, textTransform: 'uppercase' }}>Total Pending</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 900, color: 'var(--crm-text-main)', marginTop: 4 }}>{money(totalPending, currency)}</div>
              </div>
            </div>
          </div>

          <div className="cm-form-section-card">
            <div className="cm-form-section-title">Payment by Service Order</div>
            {orders.length === 0 ? (
              <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>No orders yet</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--crm-border)', textAlign: 'left' }}>
                    <th style={{ padding: '8px 12px', color: 'var(--crm-text-muted)', fontWeight: 700 }}>#</th>
                    <th style={{ padding: '8px 12px', color: 'var(--crm-text-muted)', fontWeight: 700 }}>Service</th>
                    <th style={{ padding: '8px 12px', color: 'var(--crm-text-muted)', fontWeight: 700 }}>Final</th>
                    <th style={{ padding: '8px 12px', color: 'var(--crm-text-muted)', fontWeight: 700 }}>Received</th>
                    <th style={{ padding: '8px 12px', color: 'var(--crm-text-muted)', fontWeight: 700 }}>Pending</th>
                    <th style={{ padding: '8px 12px', color: 'var(--crm-text-muted)', fontWeight: 700 }}>Status</th>
                    <th style={{ padding: '8px 12px', color: 'var(--crm-text-muted)', fontWeight: 700 }}>Next Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} style={{ borderBottom: '1px solid var(--crm-border)' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 800 }}>#{o.orderNumber}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 600 }}>{o.serviceName}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700 }}>{money(o.finalAmount, currency)}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, color: 'var(--crm-emerald)' }}>{money(o.amountReceived, currency)}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, color: 'var(--crm-text-main)' }}>{money(o.pendingAmount, currency)}</td>
                      <td style={{ padding: '10px 12px' }}><StatusBadge label={o.paymentStatus} colors={PAYMENT_COLORS} /></td>
                      <td style={{ padding: '10px 12px', color: 'var(--crm-text-muted)' }}>{fmtDDMMYYYY(o.nextPaymentDate) || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}


      {/* Add Service Order Modal */}
      {addingOrder && (
        <ServiceOrderModal
          clientName={client.clientName}
          order={null}
          availableServices={availableServices}
          currency={currency}
          onClose={() => setAddingOrder(false)}
          onSaved={handleAddOrder}
          canViewFinance={canViewFinance}
        />
      )}

      {/* Edit Service Order Modal */}
      {editingOrder && (
        <ServiceOrderModal
          clientName={client.clientName}
          order={editingOrder}
          availableServices={availableServices}
          currency={currency}
          onClose={() => setEditingOrder(null)}
          onSaved={handleEditOrder}
          canViewFinance={canViewFinance}
        />
      )}

      {/* Delete Confirm */}
      {confirmDeleteOrder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--crm-card-bg)', borderRadius: 16, padding: 32, maxWidth: 420, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 12px', color: 'var(--crm-text-main)' }}>Delete Order #{confirmDeleteOrder.orderNumber}?</h3>
            <p style={{ color: 'var(--crm-text-muted)', marginBottom: 24, fontSize: '0.9rem' }}>
              This will permanently delete the "{confirmDeleteOrder.serviceName}" order. This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="cm-btn-outline" onClick={() => setConfirmDeleteOrder(null)}>Cancel</button>
              <button className="cm-btn-outline" style={{ background: 'var(--crm-bg)', color: 'var(--crm-text-main)', borderColor: 'var(--crm-border)' }} onClick={() => handleDeleteOrder(confirmDeleteOrder.id)}>
                Yes, Delete Order
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Calendar Item Modal (Add / Edit) */}
      {(addingCalendarItem || editingCalendarItem) && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--crm-card-bg)', borderRadius: 16, padding: 32, maxWidth: 520, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 16px', color: 'var(--crm-text-main)' }}>
              {addingCalendarItem ? 'Add Calendar Entry' : 'Edit Calendar Entry'}
            </h3>
            
            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = {
                title: e.target.title.value,
                scheduledDate: e.target.scheduledDate.value,
                status: e.target.status.value,
                assignedStaff: e.target.assignedStaff.value,
                notes: e.target.notes.value,
              };
              if (addingCalendarItem) {
                await handleAddCalendarItem(formData);
              } else {
                await handleEditCalendarItem(formData);
              }
            }}>
              <div className="cm-field-group" style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Title / Description *</label>
                <input
                  name="title"
                  type="text"
                  defaultValue={editingCalendarItem ? editingCalendarItem.title : ''}
                  required
                  placeholder="e.g. Intro Reel - Launch Promo"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--crm-border)' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div className="cm-field-group">
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Scheduled Date *</label>
                  <input
                    name="scheduledDate"
                    type="date"
                    defaultValue={editingCalendarItem ? editingCalendarItem.scheduledDate : ''}
                    required
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--crm-border)' }}
                  />
                </div>
                <div className="cm-field-group">
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Status</label>
                  <select
                    name="status"
                    defaultValue={editingCalendarItem ? editingCalendarItem.status : 'Draft'}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--crm-border)' }}
                  >
                    <option value="Draft">Draft</option>
                    <option value="Shooting">Shooting</option>
                    <option value="Editing">Editing</option>
                    <option value="Scheduled">Scheduled</option>
                    <option value="Uploaded">Uploaded</option>
                  </select>
                </div>
              </div>

              <div className="cm-field-group" style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Assigned To</label>
                <select
                  name="assignedStaff"
                  defaultValue={editingCalendarItem ? editingCalendarItem.assignedStaff : ''}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--crm-border)' }}
                >
                  <option value="">— Select Staff —</option>
                  {staffList.filter((s) => s.status !== 'Inactive').map((st) => (
                    <option key={st.id} value={st.name}>{st.name}</option>
                  ))}
                </select>
              </div>

              <div className="cm-field-group" style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Notes</label>
                <textarea
                  name="notes"
                  defaultValue={editingCalendarItem ? editingCalendarItem.notes : ''}
                  placeholder="Additional scripting or editing details..."
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--crm-border)', height: 80, resize: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="cm-btn-outline" onClick={() => { setAddingCalendarItem(false); setEditingCalendarItem(null); }}>
                  Cancel
                </button>
                <button type="submit" className="cm-btn-emerald">
                  Save Entry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
